import { observer } from 'mobx-react';
import React, { ReactNode, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@wix/design-system';
import { AppContextProvider } from '../../AppLoader';
import { ButtonWithLoader } from '../../ButtonWithLoader/ButtonWithLoader';
import { EnforcementCard, EnforcementInputField } from './EnforcementCard';

interface CodeStepCardProps {
  title: ReactNode;
  subtitle: ReactNode;
  onBack: () => void;
  onVerify: () => void;
  verifyDisabled: boolean;
  field: EnforcementInputField;
  resend?: ReactNode;
  comment?: ReactNode;
  inputDataHook?: string;
  maxLength?: number;
  onlyDigits?: boolean;
  showLoader?: boolean;
}

/**
 * the shared wix-2025 "enter your code" screen for enforcement, used by the
 * SMS, email and authenticator-app code steps (identical layout — only the store
 * values differ). Purely presentational: it renders the card from props and holds no
 * store state.
 * The Verify loader is driven by the caller's `showLoader` (the store's
 * `verificationState`), matching the legacy VerifyButton: it stays on from the
 * click all the way through the post-success redirect, and only clears when a bad
 * code resets the store to Unverified.
 */
export const CodeStepCard: React.FC<CodeStepCardProps> = observer(
  ({
    title,
    subtitle,
    onBack,
    onVerify,
    verifyDisabled,
    field,
    resend,
    comment,
    inputDataHook,
    maxLength,
    onlyDigits,
    showLoader,
  }) => {
    const { t } = useTranslation();
    const {
      rootStore: {
        displayStore: { preset },
      },
    } = useContext(AppContextProvider);

    return (
      <EnforcementCard
        priority="secondary"
        title={title}
        subtitle={subtitle}
        showBackButton
        backButtonCallback={onBack}
        primaryButton={
          <ButtonWithLoader
            skin={preset.buttonDesign?.skin ?? 'standard'}
            type="submit"
            onClick={onVerify}
            disabled={verifyDisabled}
            showLoader={showLoader}
          >
            {t('verify_button')}
          </ButtonWithLoader>
        }
        secondaryButton={resend}
      >
        <Box direction="vertical" width="100%">
          <EnforcementCard.Input
            field={field}
            label={t('enter_code.code_input_placeholder')}
            dataHook={inputDataHook}
            autoFocus
            autocomplete="new-password"
            onEnterPressed={onVerify}
            maxLength={maxLength}
            onlyDigits={onlyDigits}
          />
          {comment}
        </Box>
      </EnforcementCard>
    );
  },
);
